import { HashElement } from "../../../context/map/units/unitsStore";
import { GeoZoneHashElement } from "../../../context/map/fields/fieldsStore";
import * as PIXI from "pixi.js";
import { hex2number } from "../../../utils/getColorByStatusUnit";
import { option, record } from 'fp-ts';
import { Operation } from "../../../store/reducers/references/operations";
import { groupByProp } from "@utils/utils";
import Logger from "@utils/logger";
import { pipe } from "fp-ts/es6/function";
import { store } from "../../../store";
import { actions as GeoZoneTabActions } from "../../../store/reducers/map/settings/geoZonesTab"
import PolygonDrawer from "@api/pixijs/drawers/PolygonDrawer";
import { EntityId } from "@reduxjs/toolkit";
import { geoZoneSelectors } from "../../../store/reducers/geoZones";
import L, { LatLngExpression, Point } from "leaflet";
import EventEmitter from "eventemitter3";

export enum RendererName {
    Reset = "reset_renderer",
    Operations = "operation_renderer",
    GeoZone = "geoZone_renderer",
    Unit = "unit_renderer",
    UnitTrack = "unit_track_renderer"
}

export type OperationGraphic = PIXI.Graphics & { color: string, title: string, operationId?: string }

export interface LeafletPixiApiSettings {
    zoom: number,
    project: (latlng: LatLngExpression) => Point,
    bounds?: L.LatLngBounds
    scale: number,
}
export type PixiEntitySelection = { selectedUnits: EntityId[] } | { selectedGeoZones: EntityId[] }
export interface LeafletPixiApi {
    refreshPixiContainer: () => any,
    getRendererByName<T extends MapRenderer<HashElement>>(k: T['name']): T
    root: PIXI.Container,
    getRenderers(): MapRenderer<HashElement>[]
}

export interface MapRendererService {
    refreshPixiContainer() : void
    root: PIXI.Container
    settings: LeafletPixiApiSettings
}
export interface MapRendererConfig {
    service: MapRendererService
    map: L.Map
}

export interface RendererOptions extends Record<string, any> {
}

export abstract class MapRenderer<T extends HashElement, Id = EntityId[],  E extends EventEmitter.ValidEventTypes = any> extends EventEmitter<E> {
    children: MapRenderer<T, Id, E>[] = []
    map: L.Map
    abstract name: RendererName
    abstract enabled: boolean
    abstract container: PIXI.Container | undefined
    service:  MapRendererService
    root: PIXI.Container
    settings: LeafletPixiApiSettings

    protected constructor({ map, service }: MapRendererConfig) {
        super()
        this.service = service
        this.map = map
        this.root = service.root
        this.settings = service.settings
    }

    public render<O extends RendererOptions = {}>(elements: Id, options?: O): void {
        this.children.forEach(i => i.render(elements, options))
    }

    public redraw<O extends RendererOptions = {}>(elements: Id, options?: O): void {
        this.children.forEach(i => i.redraw(elements, options))
    }

    isMatch(element: T): boolean {
        return this.enabled
    }

    public onOutsideClick() {

    }

    public onPixiOverlayChange(setting: LeafletPixiApiSettings) {
        this.settings = setting
    }

    enable(): void {
        this.enabled = true
        this.initContainer()
    }

    disable(): void {
        this.enabled = false
        this.destroyContainer()
    }

    abstract getZIndex(): number

    initContainer() {
        if (!this.container) {
            const container = new PIXI.Container()
            this.root.addChild(container)
            this.container = container;
            this.container.zIndex = this.getZIndex()
            this.container.sortableChildren = true
        } else {
            this.container.visible = true
            this.service.refreshPixiContainer()
        }
    }

    destroyContainer() {
        if (this.container) {
            this.container.visible = false
            this.service.refreshPixiContainer();
        }
    }
}

const log = Logger.getLogger("operation.renderer")

export class OperationRenderer extends MapRenderer<GeoZoneHashElement> {
    name = RendererName.Operations as const
    enabled: boolean = false
    container: PIXI.Container | undefined = undefined
    operations: { [key: string]: Operation }

    constructor(config: MapRendererConfig, { operations }: { operations: Operation[] }) {
        super(config)
        this.operations = groupByProp<Operation>('id')(operations)
    }

    override isMatch(element: GeoZoneHashElement): boolean {
        return !record.isEmpty(element.operations || {}) && super.isMatch(element)
    }

    private graphics(): OperationGraphic[] {
        if (this.container) {
            return this.container.children as OperationGraphic[]
        } else {
            return []
        }
    }

    public changeVisibility(visibleIds: string[]) {
        this.graphics().forEach(i => i.visible = visibleIds.indexOf(i.operationId || "") !== -1)
        this.service.refreshPixiContainer();
    }

    public render(geoZoneIds: EntityId[]): void {
        for (const geoZoneId of geoZoneIds) {
            const geoZone = geoZoneSelectors.selectById(store.getState().geoZones, geoZoneId)
            if (geoZone && this.isMatch(geoZone)) {
                Object.values(geoZone.operations!).forEach((operation) => {
                    const referenceOperation = record.lookup(String(operation.operationId))(this.operations)
                    const color = pipe(
                        referenceOperation,
                        option.chainNullableK(x => x.color),
                        option.getOrElse(() => "FF0000")
                    )
                    const title = pipe(
                        referenceOperation,
                        option.chainNullableK(x => x.title),
                        option.getOrElse(() => "Работа " + operation.operationId)
                    )
                    const layerId = `operation_${operation.operationId}_${geoZone.glid}`
                    if (!this.container?.getChildByName(layerId)) {
                        log.debug(`Draw some operation using ${this.name}`, operation)

                        const operationContainer: OperationGraphic = new PIXI.Graphics() as OperationGraphic
                        const operationLayer = {
                            id: String(operation.operationId),
                            title,
                            color: "#" + color
                        }
                        // TODO! Use selector
                        const currentOperations = store.getState().geoZoneTabSettings.works.current
                        operationContainer.name = layerId
                        operationContainer.operationId = String(operation.operationId)
                        operationContainer.color = color
                        operationContainer.title = title
                        operationContainer.visible = currentOperations.indexOf(operationContainer.operationId) !== -1
                        if (!this.graphics().some(i => i.operationId === operationContainer.operationId)) {
                            const operationLayer = {
                                id: String(operation.operationId),
                                title,
                                color: "#" + color
                            }
                            store.dispatch(GeoZoneTabActions.addOperationLayer(operationLayer))
                        }
                        log.debug("Add operation layer", operationLayer)
                        this.container?.addChild(operationContainer)
                        operation.coordinates.forEach(PolygonDrawer(operationContainer, this.settings, {
                            fillColor: hex2number(color)
                        }))
                    }
                })
            }
        }
    }

    getZIndex(): number {
        return 13;
    }

}